import {
    Box,
    Button,
    HStack,
    Icon,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TbArrowMerge, TbInfoCircle } from 'react-icons/tb';
import type { PartType } from '../../hooks/useGetSettings';
import {
    useRelatedInsurance,
    type InsuranceSearchResult,
} from '../../hooks/useSearchInsurance';
import { getPartTypeLabel } from '../../utils/chartUtils';
import PartBayButton from '../PartBayButton';
import CarrierSearchField from './CarrierSearchField';
import SubCompanyTable, { type SubCompanyMode } from './SubCompanyTable';
import {
    apuMergeTargetFor,
    buildApuCustom,
    buildApuOverrideDraft,
    coveredCarrierIds,
    seedApuSubModes,
    type ApuCustomEntry,
} from './apuOverrideDraft';

export interface ApuOverrideModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** The saved overrides, which the new one is merged into. */
    custom: Array<ApuCustomEntry>;
    /** Part types an override can re-key to. */
    partTypeOptions: Array<PartType>;
    /** Starting part type, so the modal opens on the org's default. */
    defaultPartType: PartType;
    onSubmit: (nextCustom: Array<ApuCustomEntry>) => void;
}

/**
 * Add one APU maximization override: which carrier, what happens to the rest of
 * its group, and what its price-matched OEM parts are re-keyed as.
 *
 * A carrier and its group are one override, not one row each — the same way a
 * DRP row covers a carrier and the sub companies riding on its rules. Picking a
 * carrier that belongs to a group asks what to do with the mates: cover them
 * here, branch one onto an override of its own, or leave it out. When a mate
 * already has an override, folding into it is offered first, because two names
 * from one group almost always re-key the same way.
 */
const ApuOverrideModal = ({
    isOpen,
    onClose,
    custom,
    partTypeOptions,
    defaultPartType,
    onSubmit,
}: ApuOverrideModalProps) => {
    const [draft, setDraft] = useState(() => buildApuOverrideDraft(defaultPartType));

    // Reopening starts a new override rather than resuming the last one.
    useEffect(() => {
        if (isOpen) setDraft(buildApuOverrideDraft(defaultPartType));
    }, [isOpen, defaultPartType]);

    const { data: relatedData } = useRelatedInsurance(draft.groupId);
    const relatedCompanies = useMemo<Array<InsuranceSearchResult>>(() => {
        if (!relatedData?.relatedCompanies || !draft.insurerId) return [];
        return relatedData.relatedCompanies.filter((company) => company._id !== draft.insurerId);
    }, [relatedData, draft.insurerId]);

    const covered = useMemo(() => coveredCarrierIds(custom), [custom]);

    // Seeded once per group so a refetch never overwrites the user's choices.
    const seededGroupRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!draft.groupId || relatedCompanies.length === 0) return;
        if (seededGroupRef.current === draft.groupId) return;
        seededGroupRef.current = draft.groupId;
        setDraft((prev) => ({ ...prev, subModes: seedApuSubModes(relatedCompanies, custom) }));
    }, [draft.groupId, relatedCompanies, custom]);

    const mergeTarget = useMemo(
        () => apuMergeTargetFor(draft, relatedCompanies, custom),
        [draft, relatedCompanies, custom],
    );

    /** Which override already covers a group mate, for the row's status line. */
    const followsNameFor = (insurerId: string): string | undefined => {
        const entry = custom.find((candidate) =>
            candidate.insuranceCompanies.some((company) => company._id === insurerId),
        );
        return entry?.insuranceCompanies.find((company) => company._id !== insurerId)?.name;
    };

    const subCompanyRows = useMemo(
        () =>
            relatedCompanies.map((company) => ({
                insurerId: company._id,
                name: company.name,
                mode: draft.subModes[company._id] ?? 'none',
                followsName: followsNameFor(company._id),
            })),
        // followsNameFor only reads `custom`, which is already a dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [relatedCompanies, draft.subModes, custom],
    );

    const hasCarrier = draft.insurerId.length > 0;
    const isCombining = !!draft.combineWithId;

    const handleSelectCarrier = (company: InsuranceSearchResult) => {
        seededGroupRef.current = undefined;
        setDraft((prev) => ({
            ...prev,
            insurerId: company._id,
            insurerName: company.name,
            groupId: company.groupId || undefined,
            subModes: {},
            combineWithId: undefined,
            combineWithName: undefined,
        }));
    };

    const handleClearCarrier = () => {
        seededGroupRef.current = undefined;
        setDraft(buildApuOverrideDraft(defaultPartType));
    };

    const handleModeChange = (insurerId: string, mode: SubCompanyMode) => {
        setDraft((prev) => ({ ...prev, subModes: { ...prev.subModes, [insurerId]: mode } }));
    };

    const handleSubmit = () => {
        if (!hasCarrier) return;
        onSubmit(buildApuCustom({ custom, draft, relatedCompanies }));
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader fontSize="md" fontWeight="semibold" color="gray.800" py={4}>
                    Add APU Maximization Override
                </ModalHeader>
                <ModalCloseButton top={3} />
                <ModalBody py={4}>
                    <VStack align="stretch" spacing={5}>
                        <Box>
                            <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1}>
                                Carrier
                            </Text>
                            {hasCarrier ? (
                                <HStack
                                    justify="space-between"
                                    align="center"
                                    gap={3}
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                    bg="white"
                                    px={4}
                                    py={3}
                                >
                                    <Text fontSize="sm" fontWeight="medium" color="gray.800" noOfLines={1}>
                                        {draft.insurerName}
                                    </Text>
                                    <HStack spacing={2} flexShrink={0}>
                                        {/* Read where the relationship is read:
                                            this carrier stops keeping an
                                            override of its own and joins the
                                            group mate that already has one. */}
                                        {mergeTarget && !isCombining && (
                                            <Tooltip
                                                label={`Add to ${mergeTarget.insurerName}'s override instead of creating another one`}
                                                placement="top"
                                                hasArrow
                                                openDelay={300}
                                            >
                                                <Button
                                                    size="xs"
                                                    variant="outline"
                                                    colorScheme="gray"
                                                    leftIcon={
                                                        <Icon
                                                            as={TbArrowMerge as React.ElementType}
                                                            boxSize={3.5}
                                                        />
                                                    }
                                                    onClick={() =>
                                                        setDraft((prev) => ({
                                                            ...prev,
                                                            combineWithId: mergeTarget.insurerId,
                                                            combineWithName: mergeTarget.insurerName,
                                                            partType: mergeTarget.partType,
                                                        }))
                                                    }
                                                >
                                                    Combine with {mergeTarget.insurerName}
                                                </Button>
                                            </Tooltip>
                                        )}
                                        <Button
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="gray"
                                            onClick={handleClearCarrier}
                                        >
                                            Change
                                        </Button>
                                    </HStack>
                                </HStack>
                            ) : (
                                <>
                                    <Text fontSize="sm" color="gray.500" mb={2.5}>
                                        Search the insurers Yaxle knows and pick one.
                                    </Text>
                                    <CarrierSearchField
                                        onSelect={handleSelectCarrier}
                                        autoFocus
                                        // The DRP flag the endpoint returns says
                                        // nothing about overrides; what matters
                                        // here is whether one already covers it.
                                        statusFor={(company) =>
                                            covered.has(company._id) ? 'Already in an override' : undefined
                                        }
                                        isSelectable={(company) => !covered.has(company._id)}
                                    />
                                    <HStack spacing={2} align="flex-start" mt={3}>
                                        <Icon
                                            as={TbInfoCircle as React.ElementType}
                                            boxSize={4}
                                            color="gray.400"
                                            mt="2px"
                                            flexShrink={0}
                                        />
                                        <Text fontSize="sm" color="gray.500">
                                            Carriers without an override re-key as the default.
                                        </Text>
                                    </HStack>
                                </>
                            )}
                        </Box>

                        {/* Folded into a group mate: the mate's override owns the
                            part type, so there is nothing left to choose. */}
                        {hasCarrier && isCombining && (
                            <Box
                                borderWidth="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                                bg="gray.50"
                                px={4}
                                py={3}
                            >
                                <HStack justify="space-between" align="center" gap={3}>
                                    <Box minW={0}>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                            Joins {draft.combineWithName}&apos;s override
                                        </Text>
                                        <Text fontSize="sm" color="gray.500" mt={0.5}>
                                            {draft.insurerName} is added to that override and
                                            re-keys as {getPartTypeLabel(draft.partType)}.
                                        </Text>
                                    </Box>
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="gray"
                                        flexShrink={0}
                                        onClick={() =>
                                            setDraft((prev) => ({
                                                ...prev,
                                                combineWithId: undefined,
                                                combineWithName: undefined,
                                            }))
                                        }
                                    >
                                        Give it its own
                                    </Button>
                                </HStack>
                            </Box>
                        )}

                        {hasCarrier && !isCombining && relatedCompanies.length > 0 && (
                            <Box>
                                <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1}>
                                    Sub companies
                                </Text>
                                <Text fontSize="sm" color="gray.500" mb={2.5}>
                                    Others in {draft.insurerName}&apos;s group. Companies on this
                                    override share one row; branch any that re-key differently.
                                </Text>
                                <SubCompanyTable
                                    parentName={draft.insurerName}
                                    sharedNoun="override"
                                    emptyMessage="No other companies in this group"
                                    maxH="220px"
                                    rows={subCompanyRows}
                                    onModeChange={handleModeChange}
                                />
                            </Box>
                        )}

                        {hasCarrier && !isCombining && (
                            <Box>
                                <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={2}>
                                    Re-key as
                                </Text>
                                <Select
                                    size="sm"
                                    w="200px"
                                    bg="white"
                                    value={draft.partType}
                                    onChange={(e) =>
                                        setDraft((prev) => ({
                                            ...prev,
                                            partType: e.target.value as PartType,
                                        }))
                                    }
                                >
                                    {partTypeOptions.map((partType) => (
                                        <option key={partType} value={partType}>
                                            {getPartTypeLabel(partType)}
                                        </option>
                                    ))}
                                </Select>
                            </Box>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <HStack spacing={2}>
                        <Button size="action" variant="outlineGray" onClick={onClose}>
                            Cancel
                        </Button>
                        <PartBayButton
                            size="action"
                            variant="solid"
                            colorScheme="partbay-blue"
                            onClick={handleSubmit}
                            isDisabled={!hasCarrier}
                            text="Add Override"
                        />
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ApuOverrideModal;
